var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var __param = (this && this.__param) || function (paramIndex, decorator) {
    return function (target, key) { decorator(target, key, paramIndex); }
};
import { StreamPartIDUtils, randomString, toUserId } from '@streamr/utils';
import random from 'lodash/random';
import sample from 'lodash/sample';
import without from 'lodash/without';
import { delay, inject, injectable } from 'tsyringe';
import { ConfigInjectionToken } from '../ConfigTypes';
import { StreamrClientError } from '../StreamrClientError';
import { StorageNodeRegistry } from '../contracts/StorageNodeRegistry';
import { convertBytesToStreamMessage } from '../protocol/oldStreamMessageBinaryUtils';
import { forEach, map, transformError } from '../utils/GeneratorUtils';
import { LoggerFactory } from '../utils/LoggerFactory';
import { pull } from '../utils/PushBuffer';
import { PushPipeline } from '../utils/PushPipeline';
import { FetchHttpStreamResponseError, createQueryString, fetchLengthPrefixedFrameHttpBinaryStream } from '../utils/utils';
import { MessagePipelineFactory } from './MessagePipelineFactory';
function isResendLast(options) {
    return options && typeof options === 'object' && 'last' in options && options.last != null;
}
function isResendFrom(options) {
    return options && typeof options === 'object' && 'from' in options && !('to' in options) && options.from != null;
}
function isResendRange(options) {
    return options && typeof options === 'object' && 'from' in options && 'to' in options && options.to && options.from != null;
}
const createUrl = (baseUrl, endpointSuffix, streamPartId, query = {}) => {
    const queryMap = {
        ...query,
        format: 'raw'
    };
    const [streamId, streamPartition] = StreamPartIDUtils.getStreamIDAndPartition(streamPartId);
    const queryString = createQueryString(queryMap);
    return `${baseUrl}/streams/${encodeURIComponent(streamId)}/data/partitions/${streamPartition}/${endpointSuffix}?${queryString}`;
};
const getHttpErrorTransform = () => {
    return async (err) => {
        let message;
        if (err instanceof FetchHttpStreamResponseError) {
            const body = await err.response.text();
            let descriptionSnippet;
            try {
                const json = JSON.parse(body);
                // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
                descriptionSnippet = `: ${json.error}`;
            }
            catch {
                descriptionSnippet = '';
            }
            message = `Storage node fetch failed${descriptionSnippet}, httpStatus=${err.response.status}, url=${err.response.url}`;
        }
        else {
            message = err?.message ?? 'Unknown error';
        }
        return new StreamrClientError(message, 'STORAGE_NODE_ERROR');
    };
};
export const toInternalResendOptions = (options) => {
    return {
        ...options,
        publisherId: (('publisherId' in options) && (options.publisherId !== undefined)) ? toUserId(options.publisherId) : undefined
    };
};
let Resends = class Resends {
    storageNodeRegistry;
    messagePipelineFactory;
    config;
    logger;
    /* eslint-disable indent */
    constructor(storageNodeRegistry, messagePipelineFactory, config, loggerFactory) {
        this.storageNodeRegistry = storageNodeRegistry;
        this.messagePipelineFactory = messagePipelineFactory;
        this.config = config;
        this.logger = loggerFactory.createLogger('Resends');
    }
    async resend(streamPartId, options, getStorageNodes, abortSignal) {
        const raw = options.raw ?? false;
        if (isResendLast(options)) {
            if (options.last <= 0) {
                const emptyStream = new PushPipeline();
                emptyStream.endWrite();
                return emptyStream;
            }
            return this.fetchStream('last', streamPartId, {
                count: options.last
            }, raw, getStorageNodes, abortSignal);
        }
        else if (isResendRange(options)) {
            return this.fetchStream('range', streamPartId, {
                fromTimestamp: new Date(options.from.timestamp).getTime(),
                fromSequenceNumber: options.from.sequenceNumber,
                toTimestamp: new Date(options.to.timestamp).getTime(),
                toSequenceNumber: options.to.sequenceNumber,
                publisherId: options.publisherId,
                msgChainId: options.msgChainId
            }, raw, getStorageNodes, abortSignal);
        }
        else if (isResendFrom(options)) {
            return this.fetchStream('from', streamPartId, {
                fromTimestamp: new Date(options.from.timestamp).getTime(),
                fromSequenceNumber: options.from.sequenceNumber,
                publisherId: options.publisherId
            }, raw, getStorageNodes, abortSignal);
        }
        else {
            throw new StreamrClientError(`can not resend without valid resend options: ${JSON.stringify({ streamPartId, options })}`, 'INVALID_ARGUMENT');
        }
    }
    async fetchStream(resendType, streamPartId, query, raw, getStorageNodes, abortSignal) {
        const traceId = randomString(5);
        this.logger.debug('Fetch resend data', {
            loggerIdx: traceId,
            resendType,
            streamPartId,
            query
        });
        const streamId = StreamPartIDUtils.getStreamID(streamPartId);
        const nodeAddresses = await getStorageNodes(streamId);
        if (!nodeAddresses.length) {
            throw new StreamrClientError(`no storage assigned: ${streamId}`, 'NO_STORAGE_NODES');
        }
        const nodeAddress = nodeAddresses[random(0, nodeAddresses.length - 1)];
        const nodeUrls = (await this.storageNodeRegistry.getStorageNodeMetadata(nodeAddress)).urls;
        const url = createUrl(sample(nodeUrls), resendType, streamPartId, query);
        const messageStream = raw ? new PushPipeline() : this.messagePipelineFactory.createMessagePipeline({
            streamPartId,
            /*
             * Disable ordering if the source of this resend is the only storage node. In that case there is no
             * other storage node from which we could fetch the gaps. When we set "disableMessageOrdering"
             * to true, we disable both gap filling and message ordering. As resend messages always arrive
             * in ascending order, we don't need the ordering functionality.
             */
            getStorageNodes: async () => without(nodeAddresses, nodeAddress),
            config: (nodeAddresses.length === 1) ? { ...this.config, orderMessages: false } : this.config
        });
        const lines = transformError(fetchLengthPrefixedFrameHttpBinaryStream(url, abortSignal), getHttpErrorTransform());
        setImmediate(async () => {
            let count = 0;
            const messages = map(lines, (bytes) => convertBytesToStreamMessage(bytes));
            await pull(forEach(messages, () => count++), messageStream);
            this.logger.debug('Finished resend', { loggerIdx: traceId, messageCount: count });
        });
        return messageStream;
    }
};
Resends = __decorate([
    injectable(),
    __param(0, inject(delay(() => StorageNodeRegistry))),
    __param(1, inject(delay(() => MessagePipelineFactory))),
    __param(2, inject(ConfigInjectionToken)),
    __metadata("design:paramtypes", [StorageNodeRegistry,
        MessagePipelineFactory, Object, LoggerFactory])
], Resends);
export { Resends };
